import { useMemo } from 'react';
import { formatFloatAsPercent } from '@gonfalon/format/formatFloatAsPercent';
import { formatNumber } from '@gonfalon/format/formatNumber';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, ProgressBar } from '@launchpad-ui/components';
import clsx from 'clsx';

import {
  DEFAULT_USAGE_PRECISION,
  USAGE_ERROR_THRESHOLD,
  USAGE_UNLIMITED,
  USAGE_WARNING_THRESHOLD,
} from '../../../../constants';
import { type UsageTabData, type UsageTabKey } from '../../../../types';
import { usageTabConfig } from '../../../constants/usageTabConfig';

import styles from './RemainingUsageCard.module.css';
import baseStyles from './UsageCardBase.module.css';

type RemainingUsageCardProps = {
  seatsUsage: number;
  seatsLimit: number;
  allUsageData?: UsageTabData[];
};

export function RemainingUsageCard({ seatsUsage, seatsLimit, allUsageData }: RemainingUsageCardProps) {
  const isLoading = allUsageData && allUsageData.some((data) => data.isLoading);

  const sortedRows = useMemo(() => {
    if (isLoading) {
      return [];
    }

    return [
      {
        tab: 'seats',
        title: msg('billing.planUsageOverview.seats'),
        total: seatsUsage,
        limit: seatsLimit,
        isError: false,
      },
      ...(allUsageData ?? []),
    ]
      .map(({ tab, title, total, limit, isError }) => {
        const remaining = getRemaining(total, limit);
        const remainingPercentage = getRemainingPercentage(remaining, limit);
        const config = tab === 'seats' ? undefined : usageTabConfig[tab as UsageTabKey];
        return { tab, title, remaining, isError, remainingPercentage, limit, precision: config?.precision };
      })
      .sort(compareUsageRows);
  }, [isLoading, seatsUsage, seatsLimit, allUsageData]);

  return (
    <div className={baseStyles.cardContainer} data-test-id="remaining-usage-card">
      <div className={baseStyles.cardHeader}>
        <h3 className={baseStyles.cardTitle}>
          <Message id="billing.usageCards.remainingThisMonth" />
        </h3>
      </div>
      <div className={styles.rows}>
        {isLoading ? (
          <ProgressBar aria-label={msg('billing.status.loadingDots')} isIndeterminate size="small" />
        ) : (
          sortedRows.map(({ tab, title, limit, remaining, remainingPercentage, isError, precision }) => (
            <RemainingUsageRow
              key={tab}
              tab={tab}
              title={title}
              limit={limit}
              remaining={remaining}
              remainingPercentage={remainingPercentage}
              isError={isError}
              precision={precision}
            />
          ))
        )}
      </div>
    </div>
  );
}

type RemainingUsageRowProps = {
  tab: UsageTabKey | string;
  title: string;
  limit: number;
  remaining: number;
  remainingPercentage: number;
  isError: boolean;
  precision?: number;
};

export function RemainingUsageRow({
  tab,
  title,
  limit,
  remaining,
  remainingPercentage,
  isError,
  precision = DEFAULT_USAGE_PRECISION,
}: RemainingUsageRowProps) {
  if (isError) {
    return <ErrorRow />;
  }

  const className = getWarningClass(remainingPercentage);
  const formattedValue = formatNumber(remaining, { maximumFractionDigits: precision });

  return (
    <div className={styles.row} data-test-id={`remaining-row-${tab}`}>
      <div className={styles.rowTitle}>{title}</div>
      <div className={clsx(className, styles.valueCell)} data-test-id="remaining-usage">
        {limit === USAGE_UNLIMITED ? '∞' : formattedValue}
      </div>
      <div className={className} data-test-id="remaining-percentage">
        {formatFloatAsPercent(remainingPercentage / 100)}
      </div>
    </div>
  );
}

function ErrorRow() {
  return (
    <div className={styles.row}>
      <div className={styles.rowTitle}>
        <Alert status="error" variant="inline">
          <Message id="billing.usageCards.errorLoadingUsage" />
        </Alert>
      </div>
    </div>
  );
}

function getRemaining(used: number, limit: number): number {
  if (limit === USAGE_UNLIMITED) {
    return Infinity;
  }
  return limit > 0 ? Math.max(0, limit - used) : 0;
}

function getRemainingPercentage(remaining: number, limit: number): number {
  return limit > 0 ? Math.ceil((remaining / limit) * 100) : 100;
}

type UsageRow = {
  tab: string;
  title: string;
  limit: number;
  remaining: number;
  remainingPercentage: number;
  isError: boolean;
  precision?: number;
};

function compareUsageRows(a: UsageRow, b: UsageRow): number {
  // Sort errors and 0-limits to the end
  if (a.isError !== b.isError) {
    return a.isError ? 1 : -1;
  }

  const limitComparison = Number(a.limit === 0) - Number(b.limit === 0);
  if (limitComparison !== 0) {
    return limitComparison;
  }

  // Then by remainingPercentage, with a tiebreaker on remaining
  return a.remainingPercentage !== b.remainingPercentage
    ? a.remainingPercentage - b.remainingPercentage
    : a.remaining - b.remaining;
}

function getWarningClass(remainingPercentage: number): string | undefined {
  if (remainingPercentage === 100 - USAGE_ERROR_THRESHOLD) {
    return baseStyles.error;
  }
  if (remainingPercentage <= 100 - USAGE_WARNING_THRESHOLD) {
    return baseStyles.warning;
  }
  return undefined;
}
